import React from 'react';

import { graphql, PageProps } from 'gatsby';

import {
  ConversionFormData,
  CRM_RequestType,
  ServiceTestRequestBody,
} from '@mewa/types';

import t from '../../../../libs/translate';
import { NavigationData } from '../../../components/header/types';
import { CantoImage } from '../../../components/strapi/canto-image';
import { setIsLocalEnv } from '../../../helpers/feature-dev';
import { LayoutType } from '../../../layouts/types';
import { sendFormToBackend } from '../../../send-forms/send-forms';
import { GlobalPageContext } from '../../../types';
import { Conversion } from '../components/conversion';
import { HashNavigation } from '../hash-navigation';
import {
  getFormSideImagesWithStrapiCantoImage,
  trackFormStep,
} from '../helper';
import { useFormState } from '../hooks/use-form-state';
import { parseDefaultFormHash } from '../location-hash-parser';
import { ConversionTranslations } from '../translations-query';

export { Head } from '../../../layouts/head';

export const query = graphql`
  query ($lang: String!, $locale: String!) {
    ...NavigationFragment
    ...ConversionTranslationsFragment
    ...BookConsultationFragment

    strapiClothServiceTestForm(locale: { eq: $locale }) {
      sideImage {
        ...CantoImageFragment
      }
    }
  }
`;

type Props = NavigationData & {
  translations: ConversionTranslations;

  strapiBookConsultation: {
    thankYouImage: CantoImage;
    sideImage: CantoImage;
  };

  strapiClothServiceTestForm: {
    sideImage: CantoImage;
  };
};

const trackStepChange = (step: number) => {
  trackFormStep(step + 1, 'cloth_service_test');
};

export const ServiceTest = ({
  data: {
    translations,
    navigationLinks,
    navigationServiceSegments,
    strapiBookConsultation,
    strapiClothServiceTestForm,
  },
  pageContext,
}: PageProps<Props, GlobalPageContext>) => {
  const formState = useFormState(CRM_RequestType.ServiceTest);

  setIsLocalEnv(pageContext.isLocal);

  const prepareFormData = (formData: ConversionFormData) => {
    const requestData: ServiceTestRequestBody = {
      ...formData,
      requestType: CRM_RequestType.ServiceTest,
      language: pageContext.lang,
      market: pageContext.market,
    };

    return sendFormToBackend(requestData);
  };

  const formSideImages = getFormSideImagesWithStrapiCantoImage(
    strapiClothServiceTestForm.sideImage,
    {},
  );

  return (
    <HashNavigation
      stepForHash={parseDefaultFormHash}
      maxAllowedStep={formState.maxStep}
      state={formState}
      renderStep={(step, formState) => {
        return (
          <Conversion
            selectedStep={step}
            formState={formState}
            formType={CRM_RequestType.ServiceTest}
            market={pageContext.market}
            lang={pageContext.lang}
            langPrefix={pageContext.langPrefix}
            onSubmit={prepareFormData}
            translations={translations}
            imageSrc={formSideImages.desktop}
            mobileImageSrc={formSideImages.mobile}
            formTitle={t(translations, 'service_test_form_title')}
            navigationLinks={navigationLinks}
            trackingEvent="form_cloth_service_test_submit"
            serviceSegmentLinks={navigationServiceSegments.nodes}
            thankYouImage={strapiBookConsultation.thankYouImage}
          ></Conversion>
        );
      }}
      onStepChange={trackStepChange}
    />
  );
};

ServiceTest.layout = LayoutType.Minimal;

export default ServiceTest;
